import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Col, ModalV2, Row, Text } from 'components';
import { differenceInMinutes } from 'date-fns';
import { Api, theme } from 'lib';
import qs from 'qs';
import { StatusBadge } from './styles';

// Subcomponents
import GameHeader from './components/GameHeader';
import GameInfo from './components/GameInfo';
import RESTPredictionsSection from './components/PredictionsSection'; // Renaming temporarily to avoid conflict with state if any, but no conflict.
import POTMSection from './components/POTMSection';
import VotesModal from './components/VotesModal';
import CommentsModal from './components/CommentsModal';
import ReactionsModal from './components/ReactionsModal';
import CreatePOTMModal from './components/CreatePOTMModal';

// Fallback only — the real values come from /admin/predictions/get so the two
// stay in step when the backend constants change.
const DEFAULT_PREDICTION_LIMITS = { minItems: 6, maxItems: 8, minScore: 0, maxScore: 20 };

// A fan's prediction is a vote on one of the score options, so the number of
// predictions made on a game is the sum of the votes across its options.
const countVotes = (items) => (items || []).reduce((sum, item) => sum + (item.votes?.length || 0), 0);

const GameDetailModal = ({ visible, closeModal, game, teamId }) => {
  const dispatch = useDispatch();

  // Same channel Api uses for its own failures, so these read as the app's
  // standard error toast instead of a blocking browser alert.
  const showError = (message) => dispatch({ type: 'HANDLE_RES', payload: { err: message } });

  const [predictions, setPredictions] = useState({ half_time: [], full_time: [] });
  const [predictionGroups, setPredictionGroups] = useState({ half_time: null, full_time: null });
  const [predictionLimits, setPredictionLimits] = useState(DEFAULT_PREDICTION_LIMITS);
  const [loadingPredictions, setLoadingPredictions] = useState(false);
  const [refreshingPredictions, setRefreshingPredictions] = useState(false);
  const [selectedPredictionType, setSelectedPredictionType] = useState(0);

  const [isEditing, setIsEditing] = useState(false);
  const [editTemplates, setEditTemplates] = useState([]);

  const [votesModalVisible, setVotesModalVisible] = useState(false);
  const [votesList, setVotesList] = useState([]);
  const [loadingVotes, setLoadingVotes] = useState(false);

  const [commentsModalVisible, setCommentsModalVisible] = useState(false);
  const [commentsList, setCommentsList] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [currentPredictionId, setCurrentPredictionId] = useState(null);

  const [reactionsModalVisible, setReactionsModalVisible] = useState(false);
  const [reactionsData, setReactionsData] = useState(null);
  const [loadingReactions, setLoadingReactions] = useState(false);
  const [selectedReactionType, setSelectedReactionType] = useState(null);
  const [currentCommentId, setCurrentCommentId] = useState(null);

  const [potmData, setPotmData] = useState(null);
  const [loadingPOTM, setLoadingPOTM] = useState(false);

  const [createPOTMModalVisible, setCreatePOTMModalVisible] = useState(false);
  const [isEditingPOTM, setIsEditingPOTM] = useState(false);
  const [teamPlayers, setTeamPlayers] = useState([]);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
  const [loadingTeamPlayers, setLoadingTeamPlayers] = useState(false);
  const [creatingPOTM, setCreatingPOTM] = useState(false);
  const [potmRewardPoints, setPotmRewardPoints] = useState(5);

  // Derived from the votes already present on the fetched items, so it needs no extra
  // request and stays in step with the refresh button.
  const predictionVoteCounts = useMemo(() => {
    const halfTime = countVotes(predictions.half_time);
    const fullTime = countVotes(predictions.full_time);

    return { half_time: halfTime, full_time: fullTime, total: halfTime + fullTime };
  }, [predictions]);

  const isGameEditable = () => {
    if (!game?.date) return false;
    return differenceInMinutes(new Date(game.date), new Date()) > 30;
  };

  useEffect(() => {
    setIsEditing(false);
  }, [selectedPredictionType]);

  useEffect(() => {
    if (visible && game?._id && teamId) {
      fetchPredictions();
      fetchPOTM();
    } else {
      setPredictions({ half_time: [], full_time: [] });
      setPredictionGroups({ half_time: null, full_time: null });
      setPotmData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, game?._id, teamId]);

  async function fetchPredictions() {
    if (!game?._id || !teamId) return;

    setLoadingPredictions(true);

    try {
      const res = await Api.get(
        `/admin/predictions/get?gameId=${game._id}&teamId=${teamId}&includeInactive=true`
      );

      console.log('Predictions Response:', res);

      const getGroups = (response) => {
        if (response?.data?.data?.groups && Array.isArray(response.data.data.groups)) {
          return response.data.data.groups;
        }
        if (response?.data?.groups && Array.isArray(response.data.groups)) {
          return response.data.groups;
        }
        if (response?.groups && Array.isArray(response.groups)) {
          return response.groups;
        }
        return [];
      };

      // Mirrors getGroups' tolerance of the varying response envelopes.
      const limits =
        res?.data?.data?.limits || res?.data?.limits || res?.limits || DEFAULT_PREDICTION_LIMITS;
      setPredictionLimits({
        minItems: limits.minItems ?? DEFAULT_PREDICTION_LIMITS.minItems,
        maxItems: limits.maxItems ?? DEFAULT_PREDICTION_LIMITS.maxItems,
        minScore: limits.minScore ?? DEFAULT_PREDICTION_LIMITS.minScore,
        maxScore: limits.maxScore ?? DEFAULT_PREDICTION_LIMITS.maxScore
      });

      const groups = getGroups(res);
      const halfTimePredictions = groups
        .filter((g) => g.type === 'half_time')
        .flatMap((g) => g.items || []);
      const fullTimePredictions = groups
        .filter((g) => g.type === 'full_time')
        .flatMap((g) => g.items || []);

      const halfTimeGroup = groups.find((g) => g.type === 'half_time') || null;
      const fullTimeGroup = groups.find((g) => g.type === 'full_time') || null;

      setPredictions({
        half_time: halfTimePredictions,
        full_time: fullTimePredictions
      });
      setPredictionGroups({
        half_time: halfTimeGroup,
        full_time: fullTimeGroup
      });
    } catch (error) {
      console.error('Error fetching predictions:', error);
      setPredictions({ half_time: [], full_time: [] });
    } finally {
      setLoadingPredictions(false);
    }
  }

  // Pulls fresh vote/comment counts. Disabled while editing, so there are never
  // unsaved rows to reconcile against the response — the next Edit picks the new
  // counts up from `predictions`.
  const handleRefreshPredictions = async () => {
    setRefreshingPredictions(true);

    try {
      await fetchPredictions();
    } finally {
      setRefreshingPredictions(false);
    }
  };

  const handleToggleActive = async (groupId) => {
    try {
      if (!groupId) return;
      await Api.put('/admin/predictions/toggleActive', { predictionGroupId: groupId });
      fetchPredictions();
    } catch (error) {
      console.error('Error toggling active status:', error);
      showError('Failed to toggle active status');
    }
  };

  async function fetchPOTM() {
    if (!game?._id || !teamId) return;

    setLoadingPOTM(true);

    try {
      const res = await Api.get(`/admin/games/potm/get?gameId=${game._id}&teamId=${teamId}`);
      console.log('POTM response:', res.data);

      let potm = null;
      if (res.data) {
        potm = res.data;
      }

      setPotmData(potm);

      if (potm && teamPlayers.length === 0) {
        fetchTeamPlayers();
      }
    } catch (error) {
      console.error('Error fetching POTM:', error);
      setPotmData(null);
    } finally {
      setLoadingPOTM(false);
    }
  }

  async function fetchTeamPlayers() {
    if (!teamId) return;
    setLoadingTeamPlayers(true);
    try {
      const res = await Api.get(`/admin/teams/players/list?teamId=${teamId}`);
      console.log('Team players response:', res);

      let playersList = [];
      if (res.data) {
        if (Array.isArray(res.data)) playersList = res.data;
      }

      setTeamPlayers(playersList);
    } catch (error) {
      console.error('Error fetching team players:', error);
    } finally {
      setLoadingTeamPlayers(false);
    }
  }

  const togglePlayerSelection = (playerId) => {
    setSelectedPlayerIds((prev) => {
      if (prev.includes(playerId)) {
        return prev.filter((id) => id !== playerId);
      }
      if (prev.length >= 11) {
        return prev; // Limit to 11
      }
      return [...prev, playerId];
    });
  };

  const handleCreatePOTM = async () => {
    if (selectedPlayerIds.length !== 11) {
      showError('Please select exactly 11 players');
      return;
    }

    setCreatingPOTM(true);
    try {
      await Api.post('/admin/games/potm/create', {
        gameId: game._id,
        teamId,
        players: selectedPlayerIds,
        rewardPoints: potmRewardPoints
      });
      setCreatePOTMModalVisible(false);
      fetchPOTM();
    } catch (error) {
      console.error('Error creating POTM:', error);
      showError('Failed to create POTM poll');
    } finally {
      setCreatingPOTM(false);
    }
  };

  const handleUpdatePOTM = async () => {
    if (selectedPlayerIds.length !== 11) {
      showError('Please select exactly 11 players');
      return;
    }

    setCreatingPOTM(true);
    try {
      await Api.post('/admin/games/potm/update', {
        gameId: game._id,
        teamId,
        players: selectedPlayerIds,
        rewardPoints: potmRewardPoints
      });
      setCreatePOTMModalVisible(false);
      fetchPOTM();
    } catch (error) {
      console.error('Error updating POTM:', error);
      showError('Failed to update POTM poll');
    } finally {
      setCreatingPOTM(false);
    }
  };

  const handleOpenUpdatePOTM = () => {
    if (!potmData) return;

    setIsEditingPOTM(true);
    setPotmRewardPoints(potmData.rewardPoints || 5);

    // Extract player IDs assuming candidates is array of { player: PlayerObj | ID, ... }
    const playerIds = (potmData.candidates || []).map((c) => {
      if (c.player && typeof c.player === 'object') {
        return c.player._id;
      }
      return c.player;
    });

    setSelectedPlayerIds(playerIds);
    setCreatePOTMModalVisible(true);

    if (teamPlayers.length === 0) {
      fetchTeamPlayers();
    }
  };

  const handleStartEdit = () => {
    const type = selectedPredictionType === 0 ? 'half_time' : 'full_time';
    const currentList = predictions[type] || [];

    const templates = currentList.map((p) => ({
      _id: p._id,
      predictionGroup: p.predictionGroup,
      home: p.predictedScore?.home ?? '',
      away: p.predictedScore?.away ?? '',
      // Items with votes or comments are frozen server-side — score locked,
      // removal blocked.
      votesCount: p.votes?.length ?? 0,
      commentsCount: p.commentsCount ?? 0,
      key: Math.random().toString(36)
    }));

    // Creating from scratch: open with the minimum number of blank rows, since
    // the backend rejects anything short of it anyway.
    while (templates.length < predictionLimits.minItems) {
      templates.push({ home: '', away: '', key: Math.random().toString(36) });
    }

    setEditTemplates(templates);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditTemplates([]);
  };

  const handleSaveEdit = async () => {
    const type = selectedPredictionType === 0 ? 'half_time' : 'full_time';
    const currentPredictions = predictions[type] || [];
    const isCreating = currentPredictions.length === 0;

    const validTemplates = editTemplates.filter((t) => t.home !== '' && t.away !== '');
    const { minItems, maxItems } = predictionLimits;

    // Blank rows are dropped above, so a full-looking list can still fall short.
    if (validTemplates.length < editTemplates.length) {
      showError('Please fill in both scores for every prediction, or remove the empty rows.');
      return;
    }

    if (validTemplates.length < minItems) {
      showError(`At least ${minItems} predictions are required.`);
      return;
    }

    if (validTemplates.length > maxItems) {
      showError(`At most ${maxItems} predictions are allowed.`);
      return;
    }

    // Matches the uniqueness rule the update endpoint enforces.
    const scoreKeys = validTemplates.map((t) => `${parseInt(t.home) || 0}-${parseInt(t.away) || 0}`);
    const duplicate = scoreKeys.find((key, idx) => scoreKeys.indexOf(key) !== idx);
    if (duplicate) {
      showError(`Each score can only be used once — ${duplicate} appears more than once.`);
      return;
    }

    // Scores are sent exactly as typed — the endpoints own the range rule, so an
    // out-of-range value must reach them and come back as a real error rather
    // than being silently coerced here.
    const toItem = (t) => ({ home: Number(t.home), away: Number(t.away) });

    let res;

    try {
      if (isCreating) {
        res = await Api.post(
          '/admin/predictions/create',
          { gameId: game._id, teamId, type, predictionItems: validTemplates.map(toItem) },
          null,
          { silent: true }
        );
      } else {
        const groupId = currentPredictions[0]?.predictionGroup;
        if (!groupId) {
          throw new Error('Prediction Group ID not found.');
        }

        const predictionItems = validTemplates.map((t) => {
          const item = toItem(t);
          if (t._id) {
            item._id = t._id;
          }
          return item;
        });

        res = await Api.put(
          '/admin/predictions/update',
          { predictionGroupId: groupId, predictionItems },
          null,
          { silent: true }
        );
      }
    } catch (error) {
      console.error(`Error ${isCreating ? 'creating' : 'updating'} predictions:`, error);
      showError(error?.message || 'Failed to save predictions');
      return;
    }

    // Api resolves rather than throwing on an HTTP error: with { silent: true } it
    // returns the error body (so we can show the server's reason instead of a
    // generic toast), or false if there was no response at all.
    const failed = !res || res.err || res.status >= 400;

    if (failed) {
      // The update endpoint returns { err, blocked: [...] } listing every option
      // it refused, e.g. scores frozen by existing votes. The toast renders plain
      // text, so these are joined inline rather than on separate lines.
      const message = res?.blocked?.length ? res.blocked.join(' · ') : res?.err;

      // Stay in edit mode so the admin can correct the scores instead of losing them.
      showError(message || 'Failed to save predictions');
      return;
    }

    setIsEditing(false);
    fetchPredictions();
  };

  const handleViewVotes = async (predictionItemId, count) => {
    setVotesModalVisible(true);

    if (count === 0) {
      setVotesList([]);
      return;
    }

    setLoadingVotes(true);
    setVotesList([]);

    try {
      const res = await Api.get(
        `/admin/predictions/votes/list?predictionItemId=${predictionItemId}&limit=50`
      );

      let votes = [];
      if (res?.data && Array.isArray(res.data)) {
        votes = res.data.map((item) => ({
          ...item.user,
          votedAt: item.votedAt
        }));
      }

      setVotesList(votes);
    } catch (error) {
      console.error('Error fetching votes:', error);
    } finally {
      setLoadingVotes(false);
    }
  };

  const handleViewComments = async (predictionId, count) => {
    setCommentsModalVisible(true);
    setCurrentPredictionId(predictionId);

    if (count === 0) {
      setCommentsList([]);
      return;
    }

    setLoadingComments(true);
    setCommentsList([]);

    try {
      const res = await Api.get(
        `/admin/comments/list?${qs.stringify({
          entityId: predictionId,
          // Must match CommentEntityTypeEnum on the server — 'prediction' is not
          // a member and the endpoint rejects it.
          entityType: 'predictionItem',
          sortType: 'oldest',
          // The endpoint defaults to 10, which silently truncated the list while
          // the button still advertised the full count.
          limit: 50
        })}`
      );

      let comments = [];
      if (Array.isArray(res)) comments = res;
      else if (res?.comments && Array.isArray(res.comments)) comments = res.comments;
      else if (res?.data && Array.isArray(res.data)) comments = res.data;
      else if (res?.data?.comments && Array.isArray(res.data.comments))
        comments = res.data.comments;

      setCommentsList(comments);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleViewReactions = async (commentId, reactionType = null) => {
    setReactionsModalVisible(true);
    setCurrentCommentId(commentId);
    setSelectedReactionType(reactionType);
    setLoadingReactions(true);
    setReactionsData(null);

    try {
      const params = {
        limit: 100,
        offset: 0
      };
      if (reactionType) {
        params.reactionType = reactionType;
      }

      const res = await Api.get(
        `/admin/comments/getReactionDetail/${commentId}?${qs.stringify(params)}`
      );

      if (res) {
        setReactionsData(res);
      }
    } catch (error) {
      console.error('Error fetching reaction details:', error);
    } finally {
      setLoadingReactions(false);
    }
  };

  if (!game) return null;

  const getStatusLabel = (status) => {
    const labels = {
      scheduled: 'Scheduled',
      'in-play': 'Live',
      finished: 'Final',
      postponed: 'Postponed',
      canceled: 'Canceled',
      abandoned: 'Abandoned',
      'not-played': 'Not Played'
    };
    return labels[status] || status;
  };

  const modalHeader = (
    <Row start centerAll gap="16px">
      <Text h2 bold>
        Game Details
      </Text>
      <StatusBadge status={game.status}>{getStatusLabel(game.status)}</StatusBadge>
    </Row>
  );

  const modalFooter = (
    <Row justifyContent="flex-end">
      <Button text="Close" onClick={closeModal} wid="200px" big bg={theme.SECONDARY} />
    </Row>
  );

  return (
    <ModalV2
      visible={visible}
      closeModal={closeModal}
      width="900px"
      header={modalHeader}
      footer={modalFooter}
    >
      <Col pad="0">
        <GameHeader game={game} />

        <GameInfo game={game} />

        <RESTPredictionsSection
          game={game}
          predictions={predictions}
          predictionGroups={predictionGroups}
          loadingPredictions={loadingPredictions}
          isEditing={isEditing}
          editTemplates={editTemplates}
          setEditTemplates={setEditTemplates}
          predictionLimits={predictionLimits}
          predictionVoteCounts={predictionVoteCounts}
          refreshingPredictions={refreshingPredictions}
          onRefreshPredictions={handleRefreshPredictions}
          selectedPredictionType={selectedPredictionType}
          setSelectedPredictionType={setSelectedPredictionType}
          canEditGame={isGameEditable()}
          onStartEdit={handleStartEdit}
          onCancelEdit={handleCancelEdit}
          onSaveEdit={handleSaveEdit}
          onViewVotes={handleViewVotes}
          onViewComments={handleViewComments}
          onToggleActive={handleToggleActive}
        />
        <POTMSection
          potmData={potmData}
          loadingPOTM={loadingPOTM}
          game={game}
          teamPlayers={teamPlayers}
          onOpenCreateModal={() => {
            setIsEditingPOTM(false);
            setCreatePOTMModalVisible(true);
            setSelectedPlayerIds([]);
            fetchTeamPlayers();
          }}
          onOpenUpdateModal={handleOpenUpdatePOTM}
        />

      </Col>

      <VotesModal
        visible={votesModalVisible}
        onClose={() => setVotesModalVisible(false)}
        votesList={votesList}
        loadingVotes={loadingVotes}
      />

      <CommentsModal
        visible={commentsModalVisible}
        onClose={() => setCommentsModalVisible(false)}
        commentsList={commentsList}
        loadingComments={loadingComments}
        predictionId={currentPredictionId}
        onViewReactions={handleViewReactions}
      />

      <ReactionsModal
        visible={reactionsModalVisible}
        onClose={() => {
          setReactionsModalVisible(false);
          setSelectedReactionType(null);
          setReactionsData(null);
          setCurrentCommentId(null);
        }}
        reactionsData={reactionsData}
        loadingReactions={loadingReactions}
        selectedReactionType={selectedReactionType}
        onViewReactions={handleViewReactions}
        currentCommentId={currentCommentId}
      />

      <CreatePOTMModal
        visible={createPOTMModalVisible}
        onClose={() => setCreatePOTMModalVisible(false)}
        creatingPOTM={creatingPOTM}
        handleCreatePOTM={handleCreatePOTM}
        handleUpdatePOTM={handleUpdatePOTM}
        isEditing={isEditingPOTM}
        potmRewardPoints={potmRewardPoints}
        setPotmRewardPoints={setPotmRewardPoints}
        selectedPlayerIds={selectedPlayerIds}
        teamPlayers={teamPlayers}
        loadingTeamPlayers={loadingTeamPlayers}
        togglePlayerSelection={togglePlayerSelection}
      />
    </ModalV2>
  );
};

export default GameDetailModal;
